import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { Button } from '@ui/buttons';
import { makeStyles } from 'tss-react/mui';
import axios from 'axios';
import { useUser } from '@client/hooks/use-user.hooks';
import { isAdmin } from '@shared/helpers/user.helpers';

const useStyles = makeStyles()((theme) => ({
  scroll: {
    height: '100%',
    overflow: 'auto',
    backgroundColor: theme.palette.background.default,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    maxWidth: 880,
    margin: '0 auto',
    padding: theme.spacing(5, 4),
  },
  title: {
    fontWeight: 700,
    letterSpacing: -0.4,
  },
  lede: {
    color: theme.palette.text.primary,
    fontSize: '1rem',
    lineHeight: 1.55,
  },
  body: {
    color: theme.palette.text.secondary,
    fontSize: '0.9375rem',
    lineHeight: 1.6,
  },
  code: {
    fontFamily: 'monospace',
    fontSize: '0.85em',
    padding: '1px 6px',
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    borderRadius: Number(theme.shape.borderRadius) * 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  cardTitle: {
    fontWeight: 600,
  },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  textarea: {
    width: '100%',
    '& .MuiInputBase-root': {
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
  },
}));

export const AdminAIInstructions: React.FC = () => {
  const user = useUser();
  const { classes } = useStyles();
  const [value, setValue] = useState<string>('');
  const disabled = useMemo(() => !user || !isAdmin(user), [user]);

  const load = useCallback(async () => {
    const { data } = await axios.get('/ai');
    const arr: string[] = data?.instructions || [];
    setValue(arr.join('\n'));
  }, []);

  useEffect(() => {
    if (!disabled) {
      load().catch(console.error);
    }
  }, [disabled, load]);

  const handleSave = useCallback(async () => {
    const arr = value
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    await axios.put('/ai', { instructions: arr });
    await load();
  }, [value, load]);

  if (disabled) {
    return (
      <Box
        sx={{
          p: 4,
        }}
      >
        <Typography variant="h6">Not allowed</Typography>
      </Box>
    );
  }

  const lineCount = value.split('\n').filter((l) => l.trim().length > 0).length;

  return (
    <Box className={classes.scroll}>
      <Box className={classes.container}>
        <Box>
          <Typography variant="h4" className={classes.title} gutterBottom>
            Comments AI (legacy)
          </Typography>
          <Typography className={classes.lede}>
            Global system-prompt rules sent on every legacy AI call.
          </Typography>
        </Box>

        <Typography className={classes.body}>
          These instructions are injected as the system message on every call
          that goes through{' '}
          <Box component="code" className={classes.code}>
            openAiService.chat
          </Box>{' '}
          and the Gemini service. That covers the legacy comments AI and the AI
          file-import pipeline (classifier, segment extraction, label
          consolidation). The new ai-chat scopes (design / project / elements /
          organization) bypass this and use their own scope-level instructions.
          An empty list falls back to the built-in defaults.
        </Typography>

        <Box className={classes.card}>
          <Box className={classes.cardHeader}>
            <Typography variant="subtitle1" className={classes.cardTitle}>
              Instructions
            </Typography>
            <Typography className={classes.hint}>
              {lineCount} {lineCount === 1 ? 'rule' : 'rules'} · one per line
            </Typography>
          </Box>
          <TextField
            className={classes.textarea}
            value={value}
            minRows={18}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter instructions, one per line. Empty list defaults to server defaults."
            multiline
          />
          <Box className={classes.actions}>
            <Button variant="outlined" onClick={() => load()}>
              Reload
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
